// src/routes/AppRouter.tsx
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from '../components/common/ErrorBoundary';

import ProtectedRoute from '../components/common/ProtectedRoute';
import AdminPortalLayout from '../components/layout/AdminPortalLayout';
import ClientPortalLayout from '../components/layout/ClientPortalLayout';
import PublicProjectsLayout from '../components/layout/PublicProjectsLayout';

import LoginPage from '../pages/login';
import RegisterPage from '../pages/RegisterPage';
import DevRegisterPage from '../pages/DevRegisterPage';
import RecoverPasswordPage from '../pages/RecoverPasswordPage';
import PublicHomePage from '../pages/PublicHomePage';
import QuienesSomosPage from '../pages/QuienesSomosPage';
import MaintenancePage from '../pages/MaintenancePage';

import BlogPage from '../pages/BlogPage';
import BlogPostPage from '../pages/BlogPostPage';

import ClientBlogsPage from '../pages/ClientBlogsPage';
import ClientDashboardPage from '../pages/ClientDashboardPage';
import ProjectsListPage from '../pages/ProjectsListPage';
import ProjectDetailPage from '../pages/ProjectDetailPage';
import PortfolioPage from '../pages/PortfolioPage';
import InvestmentManagementPage from '../pages/InvestmentManagementPage';
import ReportsPage from '../pages/ReportsPage';
import ProfilePage from '../pages/ProfilePage';
import ContactDataPage from '../pages/ContactDataPage';
import CompleteProfilePage from '../pages/CompleteProfilePage';

import AdminDashboardPage from '../pages/AdminDashboardPage';
import ManagerDashboardPage from '../pages/ManagerDashboardPage';
import AdminBlogsPage from '../pages/AdminBlogsPage';
import AdminBlogFormPage from '../pages/AdminBlogFormPage';
import AdminBlogCategoriesPage from '../pages/AdminBlogCategoriesPage';
import UserApprovalPage from '../pages/UserApprovalPage';
import SupportTicketsPage from '../pages/SupportTicketsPage';
import SystemConfigPage from '../pages/SystemConfigPage';
import AdminPaymentSettingsPage from '../pages/AdminPaymentSettingsPage';

import AdminDevelopmentsPage from '../pages/AdminDevelopmentsPage';
import AdminDevelopmentFormPage from '../pages/AdminDevelopmentFormPage';
import AdminProjectDocumentsPage from '../pages/AdminProjectDocumentsPage';
import AdminDocumentTypesPage from '../pages/AdminDocumentTypesPage';
import AdminProjectDocumentsIndexPage from '../pages/AdminProjectDocumentsIndexPage';
import AdminKycReviewPage from '../pages/AdminKycReviewPage';

import AdminInvestmentsPage from '../pages/AdminInvestmentsPage';
import AdminInvestmentDetailPage from '../pages/AdminInvestmentDetailPage';
import AdminTransferProofsPage from '../pages/AdminTransferProofsPage';
import MyTransferProofsPage from '../pages/MyTransferProofsPage';
import AdminDeveloperRequestsPage from '../pages/AdminDeveloperRequestsPage';
import AdminRolesPage from '../pages/AdminRolesPage';
import WithdrawalsPage from '../pages/WithdrawalsPage';
import AdminWithdrawalsPage from '../pages/AdminWithdrawalsPage';
import AdminBankAccountsPage from '../pages/AdminBankAccountsPage';
import DeveloperDashboardPage from '../pages/DeveloperDashboardPage';
import DeveloperProjectDocumentsPage from '../pages/DeveloperProjectDocumentsPage';
import DeveloperProjectReportsPage from '../pages/DeveloperProjectReportsPage';

import MFAVerifyPage from '../pages/MFAVerifyPage';
import MFASetupPage from '../pages/MFASetupPage';
import PendingApprovalPage from '../pages/PendingApprovalPage';
import AccountRejectedPage from '../pages/AccountRejectedPage';

import AdminAuditLogPage from '../pages/AdminAuditLogPage';

const ProjectsListRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <ClientPortalLayout>
          <ProjectsListPage />
        </ClientPortalLayout>
      </ProtectedRoute>
    );
  }

  return (
    <PublicProjectsLayout>
      <ProjectsListPage publicView />
    </PublicProjectsLayout>
  );
};

const ProjectDetailRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <ClientPortalLayout>
          <ProjectDetailPage />
        </ClientPortalLayout>
      </ProtectedRoute>
    );
  }

  return (
    <PublicProjectsLayout>
      <ProjectDetailPage publicView />
    </PublicProjectsLayout>
  );
};

const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute adminOnly={true} allowedRoles={['admin']}>
    {children}
  </ProtectedRoute>
);

const AdminDashboardRoute: React.FC = () => {
  const { user } = useAuth();

  return user?.role === 'manager' ? <ManagerDashboardPage /> : <AdminDashboardPage />;
};

const ClientOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['client']}>
    {children}
  </ProtectedRoute>
);

export const AppRouter = () => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) return <div>Cargando...</div>;

  const authenticatedRedirect =
    isAuthenticated && ['admin', 'manager'].includes(user?.role as string)
      ? '/admin/dashboard'
      : isAuthenticated && user?.role === 'developer'
        ? '/developer/panel'
        : isAuthenticated && user?.role === 'client'
          ? '/dashboard'
          : null;

  const clientFallback = user?.role === 'developer' ? '/developer/panel' : '/dashboard';

  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path="/"
          element={authenticatedRedirect ? <Navigate to={authenticatedRedirect} replace /> : <PublicHomePage />}
        />
        <Route
          path="/login"
          element={authenticatedRedirect ? <Navigate to={authenticatedRedirect} replace /> : <LoginPage />}
        />
        <Route
          path="/registro"
          element={authenticatedRedirect ? <Navigate to={authenticatedRedirect} replace /> : <RegisterPage />}
        />

        <Route path="/dev/registro" element={<DevRegisterPage />} />

        <Route path="/recuperar-contrasena" element={<RecoverPasswordPage />} />
        <Route path="/quienes-somos" element={<QuienesSomosPage />} />
        <Route path="/mantenimiento" element={<MaintenancePage />} />

        <Route path="/mfa/verify" element={<MFAVerifyPage />} />
        <Route path="/mfa/setup" element={<MFASetupPage />} />
        
        {/* User status routes */}
        <Route path="/pending-approval" element={<PendingApprovalPage />} />
        <Route path="/account-rejected" element={<AccountRejectedPage />} />

        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slugOrId" element={<BlogPostPage />} />
        <Route path="/proyectos" element={<ProjectsListRoute />} />
        <Route path="/proyectos/:id" element={<ProjectDetailRoute />} />

        <Route path="/" element={
          <ProtectedRoute>
            <ClientPortalLayout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<ClientOnlyRoute><ClientDashboardPage /></ClientOnlyRoute>} />
          <Route path="portafolio" element={<ClientOnlyRoute><PortfolioPage /></ClientOnlyRoute>} />
          <Route path="portfolio" element={<ClientOnlyRoute><Navigate to="/portafolio" replace /></ClientOnlyRoute>} />
          <Route path="mis-inversiones" element={<ClientOnlyRoute><InvestmentManagementPage /></ClientOnlyRoute>} />
          <Route path="reportes" element={<ClientOnlyRoute><ReportsPage /></ClientOnlyRoute>} />
          <Route path="blogs" element={<ClientBlogsPage />} />
          <Route path="blogs/:slugOrId" element={<BlogPostPage />} />
          <Route path="perfil" element={<ProfilePage />} />
          <Route path="complete-profile" element={<CompleteProfilePage />} />
          <Route path="perfil/datos-bancarios" element={<Navigate to="/perfil" replace />} />
          <Route path="datos-de-contacto" element={<ContactDataPage />} />
          <Route path="comprobantes-transferencia" element={<ClientOnlyRoute><MyTransferProofsPage /></ClientOnlyRoute>} />
          <Route path="retiros" element={<ClientOnlyRoute><WithdrawalsPage /></ClientOnlyRoute>} />
          <Route path="developer/panel" element={<DeveloperDashboardPage />} />
          <Route path="developer/proyectos/new" element={<AdminDevelopmentFormPage mode="create" />} />
          <Route path="developer/proyectos/:id/edit" element={<AdminDevelopmentFormPage mode="edit" />} />
          <Route path="developer/proyectos/:id/documentos" element={<DeveloperProjectDocumentsPage />} />
          <Route path="developer/proyectos/:id/reportes" element={<DeveloperProjectReportsPage />} />
          <Route path="*" element={<Navigate to={clientFallback} replace />} />
        </Route>

        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly={true}>
              <AdminPortalLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboardRoute />} />
          <Route path="gestion-inversiones" element={<Navigate to="/admin/inversiones" replace />} />

          <Route path="proyectos" element={<AdminDevelopmentsPage />} />
          <Route path="proyectos/documentos" element={<AdminProjectDocumentsIndexPage />} />
          <Route path="proyectos/new" element={<AdminDevelopmentFormPage mode="create" />} />
          <Route path="proyectos/:id/edit" element={<AdminDevelopmentFormPage mode="edit" />} />
          <Route path="proyectos/:id/documentos" element={<AdminProjectDocumentsPage />} />
          <Route path="tipos-documentos" element={<AdminDocumentTypesPage />} />
          <Route path="kyc" element={<AdminKycReviewPage />} />

          <Route path="inversiones" element={<AdminOnlyRoute><AdminInvestmentsPage /></AdminOnlyRoute>} />
          <Route path="inversiones/:id" element={<AdminOnlyRoute><AdminInvestmentDetailPage /></AdminOnlyRoute>} />
          <Route path="comprobantes-transferencia" element={<AdminTransferProofsPage />} />
          <Route path="retiros" element={<AdminOnlyRoute><AdminWithdrawalsPage /></AdminOnlyRoute>} />
          <Route path="cuentas-bancarias" element={<AdminBankAccountsPage />} />
          <Route path="solicitudes-desarrollador" element={<AdminDeveloperRequestsPage />} />

          <Route path="reportes" element={<ReportsPage />} />

          <Route path="blogs" element={<AdminBlogsPage />} />
          <Route path="blogs/new" element={<AdminBlogFormPage />} />
          <Route path="blogs/:id/edit" element={<AdminBlogFormPage />} />
          <Route path="blogs/categorias" element={<AdminBlogCategoriesPage />} />

          <Route path="aprobacion-usuarios" element={<UserApprovalPage />} />
          <Route path="soporte" element={<AdminOnlyRoute><SupportTicketsPage /></AdminOnlyRoute>} />
          <Route path="configuracion" element={<AdminOnlyRoute><SystemConfigPage /></AdminOnlyRoute>} />
          <Route path="pagos" element={<AdminPaymentSettingsPage />} />
          <Route path="roles" element={<AdminOnlyRoute><AdminRolesPage /></AdminOnlyRoute>} />
          <Route path="perfil" element={<ProfilePage />} />

          <Route path="bitacora" element={<AdminOnlyRoute><AdminAuditLogPage /></AdminOnlyRoute>} />

          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
};

export default AppRouter;
